import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, TimeoutError } from 'rxjs';
import {
  retry,
  catchError,
  switchMap,
  filter,
  take,
  takeWhile,
} from 'rxjs/operators';
import { IdPResponseError } from '@enums/idpresponseerrors.enum';
import { Router } from '@angular/router';
import { ROUTES_ADMIN } from '@constants/routes';
import { TranslationService } from '@providers/translation-new/translation.service';
import { StorageService } from '@providers/storage/storage.service';
import { ConfirmCredentialsComponent } from '@components/confirm-credentials/confirm-credentials.component';
import { MethodsGeneralService } from '@providers/methods-general/methods-general/methods-general';
import { ModalService } from '@providers/modal-services/modal.service';
import { SessionDataProvider } from '@providers/session-data/session-data';
import { RequestErrors } from '@enums/status.enum';
import { oneRetryReqs } from '@data/network.data';
import { Platform } from '@angular/cdk/platform';
import { SnackbarService } from '@providers/snackbar-services/snackbar.services';
import { UserManagerService } from '@providers/user-manager/user-manager.service';
import { StorageKeys } from '@enums/storage.enum';
import { LoginService } from 'src/endpoints/login.service';
import { IdpAccessTokenResponse } from '@models/responses/login-response';
import { StatsService } from 'src/endpoints/stats.service';
import { ResponseToken } from '@models/responses/stats-response';
import { environment } from '@environments/environment';

const MAX_RETRIES = 2;
const MIN_RETRIES_WEB = 0;
const MIN_RETRIES_MOBILE = 1;

@Injectable({
  providedIn: 'root',
})
export class ErrorInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<unknown> =
    new BehaviorSubject<unknown>(null);

  private isRefreshingStats = false;
  private refreshTokenStatsSubject: BehaviorSubject<unknown> =
    new BehaviorSubject<unknown>(null);

  constructor(
    private loginService: LoginService,
    private methodGenerals: MethodsGeneralService,
    private modalService: ModalService,
    private platform: Platform,
    private router: Router,
    private sessionData: SessionDataProvider,
    private snackbar: SnackbarService,
    private statsService: StatsService,
    private storage: StorageService,
    private translation: TranslationService,
    private userManager: UserManagerService
  ) {}

  private setRetries(url, method): number {
    const isOneTry = Object.entries(oneRetryReqs).some(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([_, value]) => url.includes(value) && method !== 'GET'
    );
    if (!isOneTry) {
      return MAX_RETRIES;
    }
    // TODO - REVIEW IN A MOBILE WEB
    return this.platform.isBrowser ? MIN_RETRIES_WEB : MIN_RETRIES_MOBILE;
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const handleRequest = next.handle(request).pipe(
      retry(this.setRetries(request.url, request.method)),
      catchError((errorHttp: HttpErrorResponse) => {
        const { alerts, login } = this.translation.getLangMessagesByKeys(['alerts', 'login']);
        const { status, error } = errorHttp;

        if (errorHttp instanceof TimeoutError) {
          throw null;
        }

        if (status === RequestErrors.Unknown) {
          const statusConnection = window.navigator.onLine;
          if (!statusConnection) {
            const { errDTSTitle, errDTSDescription } = alerts;
            this.snackbar.showSnackBar(errDTSTitle, errDTSDescription);
            throw null;
          } else {
            const { erroServerTitle, erroServerDescription } = alerts;
            this.snackbar.showSnackBar(
              erroServerTitle,
              erroServerDescription
            );
            throw null;
          }
        } else {
          switch (status) {
            case RequestErrors.Unauthorized: {
              if (request.url.includes(environment.serverStats)) {
                return this.handle403Stats(request, next);
              } else {
                return this.handle401Error(request, next);
              }
            }
            case RequestErrors.Forbidden: {
              this.router.navigate([ROUTES_ADMIN.SUSPENDED_ACCESS], {
                replaceUrl: true,
              });
              throw null;
            }
            case RequestErrors.ServiceUnavailable:
            case RequestErrors.BadGateway:
              throw {
                title: alerts.errorMaintenanceTitle,
                message: alerts.errorMaintenanceDescription,
                code: error?.code,
                data: error?.data
              };
            case RequestErrors.GatewayTimeout:
              throw {
                title: alerts.errorTimeoutTitle,
                message: alerts.errorTimeoutDescription,
                code: error?.code,
                data: error?.data
              };
            case RequestErrors.NotFound:
              throw {
                title: alerts.title_err,
                message: alerts.passerErrorMessage,
                code: error?.code,
                data: error?.data
              };
            case RequestErrors.BadRequest: {
              const {
                title_err: title,
                passerErrorMessage: message,
              } = alerts;
              if (error?.error) {
                const {
                  alerts: {
                    passerIdpErrorInvalidGrant,
                    passerIdpErrorInvalidManagementGroup,
                    passerIdpErrorInvalidSubscription
                  }
                } = login;
                switch (error?.error) {
                  case IdPResponseError.InvalidGrant:
                    throw {
                      title,
                      message: passerIdpErrorInvalidGrant,
                      data: error?.error,
                      code: error?.code
                    };
                  case IdPResponseError.InvalidManagementGroup:
                    throw {
                      title,
                      message: passerIdpErrorInvalidManagementGroup,
                      data: error?.error,
                      code: error?.code
                    };
                  case IdPResponseError.InvalidSubscription:
                    throw {
                      title,
                      message: passerIdpErrorInvalidSubscription,
                      data: error?.error,
                      code: error?.code
                    };
                }
              }
              const response = {
                title: error?.message || title,
                message: error?.description || message,
                code: error?.code,
                data: error?.data
              };
              throw response;
            }
            default: {
              const {
                title_err: title,
                passerErrorMessage: message,
              } = alerts;
              const response = {
                title: error?.message || title,
                message: error?.description || message,
                code: error?.code,
                data: error?.data
              };
              throw response;
            }
          }
        }
      })
    );

    return handleRequest.pipe(
      takeWhile(() => {
        return window.navigator.onLine;
      })
    ) as Observable<HttpEvent<unknown>>;
  }

  private handle403Stats(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<any> {
    if (!this.isRefreshingStats) {
      this.isRefreshingStats = true;
      this.refreshTokenStatsSubject.next(null);

      return this.statsService
        .getTokenStatObservable()
        .pipe(
          switchMap((tokenResponse: ResponseToken) => {
            const { success, data, message, description } = tokenResponse;
            if (success) {
              this.isRefreshingStats = false;
              this.sessionData.tokenStats = data.accessToken;
              this.storage.setData(StorageKeys.tokenStats, data.accessToken);
              this.refreshTokenStatsSubject.next(data);
              const authorization = `Bearer ${data.accessToken}`;
              return this.success401Return(request, next, authorization);
            } else {
              this.snackbar.showSnackBar(message, description);
              this.isRefreshingStats = false;
              return null;
            }
          }),
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          catchError(_ => {
            this.isRefreshingStats = false;
            return null;
          })
        );
    }
    return this.refreshTokenStatsSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(() => {
        const authorization = `Bearer ${this.sessionData.tokenStats}`;
        return this.success401Return(request, next, authorization);
      })
    );
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const { refreshTokenIdp, subscriptionIdp } = this.sessionData;

      if (refreshTokenIdp) {
        return this.loginService
          .refreshToken(refreshTokenIdp, subscriptionIdp?.managementGroup?._id, subscriptionIdp?._id)
          .pipe(
            switchMap((tokenResponse: IdpAccessTokenResponse) => {
              this.isRefreshing = false;
              this.sessionData.token = tokenResponse.access_token;
              this.sessionData.tokenType = tokenResponse.token_type;
              this.sessionData.refreshTokenIdp = tokenResponse.refresh_token;
              this.sessionData.idpAccessToken = tokenResponse;
              this.storage.setData(StorageKeys.TOKEN, this.sessionData.token);
              this.storage.setData(StorageKeys.TOKEN_TYPE, this.sessionData.tokenType);
              this.storage.setData(StorageKeys.REFRESH_TOKEN_IDP, this.sessionData.refreshTokenIdp);
              this.storage.setData(StorageKeys.idpAccessToken, this.sessionData.idpAccessToken);
              this.refreshTokenSubject.next(tokenResponse);
              return this.success401Return(request, next);
            }),
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            catchError(_ => {
              return this.modalService
                .showAlertCompReturnObs(ConfirmCredentialsComponent, {})
                .pipe(
                  switchMap((reLogin: boolean) => {
                    if (reLogin) {
                      this.isRefreshing = false;
                      this.refreshTokenSubject.next(this.sessionData.idpAccessToken ?? true);
                      return this.success401Return(request, next);
                    } else {
                      this.loginService.unauthorized = true;
                      return this.logOutAction();
                    }
                  })
                );
            })
          );
      } else {
        return this.logOutAction();
      }
    }
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(() => this.success401Return(request, next))
    );
  }

  success401Return(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    authorization = null
  ): Observable<HttpEvent<any>> {
    const { token, tokenType, subscriptionIdp } = this.sessionData;
    const { currentlanguage } = this.translation;
    const authHeader = authorization ?? this.methodGenerals.setAuthorizationRetry(
      request.headers.get('Authorization') ?? '',
      token,
      tokenType
    );
    return next.handle(
      this.methodGenerals.getHeaders(
        request,
        token,
        authHeader,
        currentlanguage,
        this.userManager.currentUser?.sub ?? this.userManager.temporaryIdSub,
        subscriptionIdp?._id,
        subscriptionIdp?.managementGroup?._id,
        request.body
      )
    );
  }

  logOutAction(): Observable<HttpEvent<unknown>> {
    this.loginService.logoutMethod();
    this.isRefreshing = false;
    throw null;
  }
}
